export enum AnalyticsStepNumber {
  Simulation = '1 -- Simulation',
  PersonalData = '2 -- Personal Data',
  LaborData = '3 -- Laboral Data',
  Shipping = '4 -- Operation Shipping',
  Payment = '5 -- Payment',
  Signature = '6 -- Signature',
}

export enum AnalyticsOrigination {
  App = 'App',
  Web = 'Web',
  Customer = 'Customer',
}

export interface SimulationAnalyticsProps {
  simulation_amount: number;
  simulation_max_term: number;
  simulation_min_term: number;
  simulation_term_count: number;
  simulation_term: number;
  simulation_offer_count: number;
  simulation_cex_available: boolean;
  simulation_sus_available: boolean;
  simulation_pls_available: boolean;
}

export interface CheckoutAnalyticsProps {
  currency: string;
  value: number;
  coupon?: string;
  payment_type?: string;
  items: ItemsAnalytics;
}

export interface ItemsAnalytics {
  item_id?: string;
  item_name?: string;
}

export interface EntryContinueAnalytics {
  time_elapsed: number;
  simulation_amount: number;
  simulation_term: number;
  simulation_tae: number;
  simulation_tin: number;
  simulation_regular_fee: number;
  simulation_first_fee: number;
  simulation_total_cost: number;
  simulation_opening_fee: number;
  simulation_opening_fee_type: string;
  simulation_flow?: string;
  step_number: AnalyticsStepNumber;
}

export interface NfcSuccessAnalytics {
  time_elapsed: number;
  time_reading: number;
  document_type: string;
  step_number: AnalyticsStepNumber;
}

export interface NfcSkipAnalytics {
  time_elapsed: number;
  step_number: AnalyticsStepNumber;
}

export interface PersonalDataContinueAnalytics {
  time_elapsed: number;
  step_number: AnalyticsStepNumber;
  cust_age_segment?: string;
  cust_birth_country?: string;
  cust_nationality?: string;
  cust_email_domain?: string;
  cust_classification?: string;
  prop_require_documentation?: boolean;
  prop_require_labor_data?: boolean;
  prop_require_secure_payment?: boolean;
  prop_require_address_data?: boolean;
  prop_require_enrichment_personal_finances?: boolean;
  prop_source_application?: string;
  prop_product?: string | null;
  prop_amount?: number;
  prop_refinances_amount?: number;
}

export interface LaborDataContinueAnalytics {
  time_elapsed: number;
  step_number: AnalyticsStepNumber;
  cust_contract_type: string;
  cust_monthly_income: string;
  cust_province: string;
  cust_postal_code: string;
  used_manual_input: boolean;
}

export interface ShippingContinueAnalytics {
  time_elapsed: number;
  step_number: AnalyticsStepNumber;
  continue_mode: ShippingContinueMode;
}

export enum ShippingContinueMode {
  SMS = 'SMS',
  EMAIL = 'EMAIL',
  SAMEDEVICE = 'SAMEDEVICE',
}

export interface PaymentContinueAnalytics {
  time_elapsed: number;
  step_number: AnalyticsStepNumber;
  payment_type: PaymentMethodAnalytics;
  has_previous_cards: boolean;
  amount: number;
  third: string;
}

export enum PaymentMethodAnalytics {
  WALLET = 'WALLET',
  CARD = 'CARD',
  REUSED = 'REUSED',
}

export interface SignatureAnalyticsProps {
  time_elapsed: number;
  step_number?: AnalyticsStepNumber;
  third?: string;
}

export enum PaymentThirdParty {
  SPREEDLY = 'SPREEDLY',
  ZRU = 'ZRU',
}

export enum SignatureThirdParty {
  GALEON = 'GALEON',
  RUBRICAE = 'RUBRICAE',
  ECERTIC = 'ECERTIC',
  PEPPER = 'PEPPER',
}

export interface PurchaseAnalytics {
  currency: string;
  transaction_id: string;
  value: number;
  coupon?: string;
  shipping?: number;
  tax?: number;
  items: ItemsAnalytics;
}

export interface GenerateLeadAnalytics {
  currency: string;
  value: number;
}

export enum UnavailableCustomerAnalytics {
  Profile = 'PROFILE',
  Unavailable = 'UNAVAILABLE',
}

// Valores admitidos para el campo notification_advice del banner de notificaciones del dashboard de operaciones.
// El orden coincide con la prioridad de la regla (1..8) que se reporta en notification_message.
export enum NotificationAdvice {
  INACTIVE_2M = 'Inactivo 2 meses',
  INACTIVE_1M = 'Inactivo mes',
  LOW_VS_PREV_MONTH = 'Bajo vs mes anterior',
  LOW_VS_BEST_6M = 'Bajo vs mejor 6 meses',
  GOOD_VS_BEST_6M = 'Bien vs mejor 6 meses',
  GOOD_VS_PREV_MONTH = 'Bien vs mes anterior',
  PENDING_APPROVED = 'Aprobadas',
  PENDING_CAPTURE = 'En captura',
  // Avisos por evento (Pepper One)
  ONE_REACTIVATION = 'one_reactivation',
  ONE_DENIED = 'one_denied',
  ONE_FIRST = 'one_first',
  ONE_COUNTER_OFFER = 'one_counteroffer',
  ONE_HIGH_AMOUNT = 'one_high_amount',
  ONE_REJECTED_COUNTER_OFFER = 'one_rejected_counteroffer',
}

export interface ShowNotificationBannerAnalytics {
  notification_date: string;
  notification_message: number;
  notification_advice: NotificationAdvice;
}

export enum ClientTaxonomyProps {
  USER_ID = 'UserId',
  SESSION_ID = 'SessionId',
  MERC_HAS_ECO_OFFERS = 'merc_has_ECO_offers',
  MERC_HAS_CEX_OFFERS = 'merc_has_CEX_offers',
  MERC_HAS_CBR_OFFERS = 'merc_has_CBR_offers',
  MERC_HAS_SUS_OFFERS = 'merc_has_SUS_offers',
  MERC_LEVEL = 'merc_level',
  MERC_SECTOR = 'merc_sector',
  MERC_SALES_TYPE = 'merc_sales_type',
  MERC_COMMERCIAL_AGENT = 'merc_commercial_agent',
  MERC_COMMERCIAL_NAME = 'merc_commercial_name',
  OFFERS_MAX_AMOUNT = 'offers_max_amount',
  OFFERS_MIN_AMOUNT = 'offers_min_amount',
  OFFERS_MAX_TERM = 'offers_max_term',
  OFFERS_MIN_TERM = 'offers_min_term',
  OFFERS_COUNT = 'offers_count',
  MTD_ACT_AMOUNT_SUM = 'MTD_ACT_amount_sum',
  MTD_ACT_LOANS_COUNT = 'MTD_ACT_loans_count',
  LM_ACT_AMOUNT_SUM = 'LM_ACT_amount_sum',
  LM_ACT_LOANS_COUNT = 'LM_ACT_loans_count',
  SUPER_EXPRESS_ENABLED = 'super_express_enabled',
  AG_CODE_ENABLED = 'AG_code_enabled',
}

export enum ClientTaxonomyEvents {
  ORIGIN = 'origin',
  CREDIT_PROPOSAL_ID = 'credit_proposal_id',
  LOGIN = 'login',
  PASSWORD_RESET = 'password_reset',
  PASSWORD_CHANGE = 'password_change',
  BIOMETRIC_ENABLEMENT = 'biometric_enablement',
  LOGOUT = 'logout',
  NOTIFICATION_ENABLEMENT = 'notification_enablement',
  NOTIFICATION_DISABLEMENT = 'notification_disablement',
  REFUND_SIMULATION = 'refund_simulation',
  REFUND_CONFIRMATION = 'refund_confirmation',
  OPEN_PEPPERONE = 'open_pepperone',
  CONTACT = 'contact',
  SUPER_EXPRESS_ENABLEMENT = 'super_express_enablement',
  AG_CODE_ENABLEMENT = 'ag_code_enablement',
  // Origination Taxonomy - Generales
  TRY_EXIT = 'try_exit',
  BACK_EXIT = 'back_exit',
  EVENT_KO = 'event_ko',
  LOAN_REFUSED = 'loan_refused',
  // RESUMPTION = 'resumption',
  // Origination Taxonomy - Entry
  SIMULATION = 'simulation',
  EDIT_SIMULATION = 'edit_simulation',
  CHANGE_SIMULATION = 'change_simulation',
  CONTINUE_ENTRY = 'continue_entry',
  BEGIN_TRANSACTION = 'begin_transaction',
  // Origination Taxonomy - Lector DNI NFC
  READ_NFCID_SUCCESS = 'read_nfcid_success',
  READ_NFCID_ERROR = 'read_nfcid_error',
  SKIP_NFCID = 'skip_nfcid',
  // Origination Taxonomy - Datos Personales
  BACK_ENTRY = 'back_entry',
  BELENDER_UNAVAILABLE = 'belender_unavailable',
  CONTINUE_PDATA = 'continue_pdata',
  // Origination Taxonomy - Datos Laborales
  CONTINUE_LDATA = 'continue_ldata',
  MANUAL_INPUT_AVAILABLE = 'manual_input_available',
  CONTINUE_SHIPPING = 'continue_shipping',
  // Origination Taxonomy - OCR
  CONTINUE_OCR = 'continue_ocr',
  SEND_OCR = 'send_ocr',
  INVALID_OCR = 'invalid_ocr',
  VALID_OCR = 'valid_ocr',
  // Origination Taxonomy - Bankreader
  SHOW_LEGAL_BKR = 'show_legal_bkr',
  CONTINUE_BKR_TC = 'continue_bkr_t&c',
  CONTINUE_BANK_SELECTION = 'continue_bank_selection',
  CONTINUE_BKR_CREDENTIALS = 'continue_bkr_credentials',
  CONTINUE_BKR_SUCCESS = 'continue_bkr_success',
  CONTINUE_BKR_SET_ACCOUNT = 'continue_bkr_set_account',
  BKR_IDENTIFICATION_ERROR = 'bkr_identification_error',
  // Origination Taxonomy - Belender
  SHOW_LEGAL_BEL = 'show_legal_bel',
  INVALID_PHONE_BEL = 'invalid_phone_bel',
  RETRY_PHONE_BEL = 'retry_phone_bel',
  RESEND_PHONE_BEL = 'resend_sms_bel',
  PIN_ERROR_BEL = 'pin_error_bel',
  CONTINUE_BEL = 'continue_bel',
  // Origination Taxonomy - TPV
  ADD_PAYMENT_INFO = 'add_payment_info',
  CONTINUE_PAYMENT = 'continue_payment',
  PAYMENT_ERROR = 'payment_error',
  // Origination Taxonomy - Firma
  SHOW_CONTRACT = 'show_contract',
  RESEND_OTP = 'resend_otp',
  PIN_ERROR = 'pin_error',
  CONTINUE_SIGNATURE = 'continue_signature',
  // Origination Taxonomy - Finales
  VERIFY = 'verify',
  PURCHASE = 'purchase',
  GENERATE_LEAD = 'generate_lead',
  // Operations Dashboard - Banner de notificaciones
  SHOW_NOTIFICATION_BANNER = 'show_notification_banner',
  CLICK_NOTIFICATION_BANNER_CTA = 'click_notification_banner_CTA',
  CLOSE_NOTIFICATION_BANNER = 'close_notification_banner',
}

export enum ClarityTaxonomyProps {
  STEP_NUMBER = 'step_number',
  TIME_ELAPSED = 'time_elapsed',
  SIMULATION_AMOUNT = 'simulation_amount',
  SIMULATION_MAX_TERM = 'simulation_max_term',
  SIMULATION_MIN_TERM = 'simulation_min_term',
  SIMULATION_TERM = 'simulation_term',
  SIMULATION_TAE = 'simulation_tae',
  SIMULATION_TIN = 'simulation_tin',
  SIMULATION_REGULAR_FEE = 'simulation_regular_fee',
  SIMULATION_FIRST_FEE = 'simulation_first_fee',
  SIMULATION_TOTAL_COST = 'simulation_total_cost',
  SIMULATION_OPENING_FEE = 'simulation_opening_fee',
  SIMULATION_OPENING_FEE_TYPE = 'simulation_opening_fee_type',
  SIMULATION_FLOW = 'simulation_flow',
  TIME_READING = 'time_reading',
  DOCUMENT_TYPE = 'document_type',
  TYPE = 'type',
  CUST_AGE_SEGMENT = 'cust_age_segment',
  CUST_BIRTH_COUNTRY = 'cust_birth_country',
  CUST_NATIONALITY = 'cust_nationality',
  CUST_EMAIL_DOMAIN = 'cust_email_domain',
  CUST_CLASSIFICATION = 'cust_classification',
  PROP_REQUIRE_DOCUMENTATION = 'prop_require_documentation',
  PROP_REQUIRE_LABOR_DATA = 'prop_require_labor_data',
  PROP_REQUIRE_SECURE_PAYMENT = 'prop_require_secure_payment',
  PROP_REQUIRE_ADDRESS_DATA = 'prop_require_address_data',
  PROP_REQUIRE_ENRICHMENT_PERSONAL_FINANCES = 'prop_require_enrichment_personal_finances',
  PROP_SOURCE_APPLICATION = 'prop_source_application',
  PROP_PRODUCT = 'prop_product',
  PROP_AMOUNT = 'prop_amount',
  PROP_REFINANCED_AMOUNT = 'prop_refinances_amount',
  CUST_CONTRACT_TYPE = 'cust_customer_type',
  CUST_MONTHLY_INCOME = 'cust_monthly_income',
  CUST_PROVINCE = 'cust_province',
  CUST_POSTAL_CODE = 'cust_postal_code',
  USED_MANUAL_INPUT = 'used_manual_input',
  CONTINUE_MODE = 'continue_mode',
  THIRD = 'third',
  DEVICE = 'device',
  ATTEMPT_NUMBER = 'attempt_number',
  HAS_PREVIOUS_CARDS = 'has_previous_cards',
  PAYMENT_TYPE = 'payment_type',
  AMOUNT = 'amount',
  CURRENCY = 'currency',
  TRANSACTION_ID = 'transaction_id',
  VALUE = 'value',
  ITEM_ID = 'item_id',
  ITEM_NAME = 'item_name',
}

export interface TaxonomyEventGeneralProps {
  origin: string;
  uuid?: string;
}
